/**
 * The menu on a column header: things you do to a whole column at once.
 *
 * Every operation here is stored as a rule against the column rather than as
 * millions of changed cells. That is not an optimisation detail, it is what
 * makes the operations possible at all — "replace every X with Y" across five
 * million rows is one row on the server, and it can be taken back by deleting
 * that row rather than by undoing five million writes.
 *
 * Rules stack in the order they are added, because they do not commute:
 * trimming then upper-casing is not the same as the reverse.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ActionIcon, Alert, Button, Divider, Group, Loader, Menu, Modal, ScrollArea, Select, Stack,
  Switch, Table, Text, TextInput,
} from '@mantine/core';
import {
  IconArrowLeft, IconArrowRight, IconChevronDown, IconColumnInsertRight,
  IconColumnRemove, IconCursorText, IconEraser, IconFilter, IconLetterCase,
  IconAddressBook, IconChartBar, IconCopy, IconLock, IconMathFunction, IconPhoneCheck, IconReplace, IconScissors, IconSortAscending,
  IconSortDescending, IconTextWrap,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useAddSheetColumn, useAddSheetTransform, useDedupeSheet, useDropSheetColumn, useMatchLeads,
  useRenameSheetColumn, useSheetDuplicates, useSheetSummary, useVerifySheetColumn,
  type SheetFilter,
} from '../api/queries';
import { NewFileFields, fileStem, useNewFileDetails, useOpenNewFile } from './NewFileFields';
import { TOKENS, formatNumber } from '../theme';
import { useVocab } from '../vocab';

/** What a filter can do. Kept in step with the operators the server implements. */
const OPS = [
  { value: 'contains', label: 'contains' },
  { value: 'eq', label: 'is exactly' },
  { value: 'ne', label: 'is not' },
  { value: 'starts', label: 'starts with' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
  // Comparing by size. A cell that does not hold a number never matches
  // these, so "is less than 100" does not quietly return the blanks.
  { value: 'gt', label: 'is more than' },
  { value: 'gte', label: 'is at least' },
  { value: 'lt', label: 'is less than' },
  { value: 'lte', label: 'is at most' },
  { value: 'between', label: 'is between' },
  // Finding what is broken, which is most of why a lead file gets filtered.
  { value: 'bad_email', label: 'is not a valid email' },
  { value: 'bad_phone', label: 'is not a usable UK phone number' },
];

/** Operators that compare by size and so want a number, not text. */
const NUMERIC = new Set(['gt', 'gte', 'lt', 'lte', 'between']);

/** Operations that need no input and can be applied straight from the menu. */
const DIRECT = [
  { kind: 'trim', label: 'Trim spaces', icon: IconScissors },
  { kind: 'upper', label: 'UPPERCASE', icon: IconLetterCase },
  { kind: 'lower', label: 'lowercase', icon: IconLetterCase },
] as const;

type Asking =
  | {
      kind:
        | 'replace' | 'fill_empty' | 'clear' | 'verify' | 'filter' | 'rename' | 'add'
        | 'duplicates' | 'summary' | 'match_leads';
    }
  | { kind: 'hash'; algorithm: 'md5' | 'sha256' }
  | null;

export function SheetColumnMenu({
  sheetId,
  column,
  disabled,
  onSort,
  onFilter,
  onMove,
  onCompute,
  onFormula,
  canMoveLeft,
  canMoveRight,
  baseVersion,
  columns,
  fileName,
}: {
  sheetId: string | null;
  /** The file's name, to suggest one for a file this makes. */
  fileName: string;
  column: string;
  /** Every column in the file, for pickers that reference another one. */
  columns: string[];
  /** Bulk edits need a writable sheet; sorting and filtering do not. */
  disabled: boolean;
  onSort: (dir: 'asc' | 'desc') => void;
  onFilter: (f: SheetFilter) => void;
  /** Move this column one place. Absent at the ends, where there is nowhere to go. */
  onMove: (by: -1 | 1) => void;
  /** Open the worked-out-column dialog, with this column pre-selected. */
  onCompute: () => void;
  onFormula: () => void;

  canMoveLeft: boolean;
  canMoveRight: boolean;
  /** The version any change is computed from, so a stale one is refused. */
  baseVersion: number;
}) {
  const add = useAddSheetTransform(sheetId);
  const verify = useVerifySheetColumn(sheetId);
  const rename = useRenameSheetColumn(sheetId);
  const addColumn = useAddSheetColumn(sheetId);
  const dropColumn = useDropSheetColumn(sheetId);
  const [name, setName] = useState('');
  const [asking, setAsking] = useState<Asking>(null);
  const [find, setFind] = useState('');
  const [replace, setReplace] = useState('');
  const [op, setOp] = useState('contains');
  // Emails are lower-cased by everyone; reference numbers are not. Defaulting
  // on, because email is what these lists usually hold.
  const [hashLower, setHashLower] = useState(true);
  /* The upper bound, for `between` only. */
  const [upper, setUpper] = useState('');

  const [needle, setNeedle] = useState('');

  /*
    Whether the form is answerable as it stands.

    A comparison wants a number rather than any text at all, because a filter
    that silently matches nothing reads as "there are no rows over 1000" rather
    than "that was not a number".
  */
  const looksNumeric = (t: string) => t.trim() !== '' && !Number.isNaN(Number(t.replace(/[^0-9.eE+-]/g, '')));
  const ready =
    op === 'empty' || op === 'not_empty'
      ? true
      : op === 'between'
        ? looksNumeric(needle) && looksNumeric(upper)
        : NUMERIC.has(op)
          ? looksNumeric(needle)
          : needle !== '';

  function applyFilter() {
    if (!ready) return;
    onFilter({ col: column, op, value: needle, ...(op === 'between' ? { value2: upper } : {}) });
    setAsking(null);
  }
  // Only fetched while the dialog is open: it is a scan of the file.
  const duplicates = useSheetDuplicates(sheetId, asking?.kind === 'duplicates' ? column : null);
  const summary = useSheetSummary(sheetId, asking?.kind === 'summary' ? column : null);
  const openNewFile = useOpenNewFile();
  const dedupeDetails = useNewFileDetails(
    asking?.kind === 'duplicates',
    `${fileStem(fileName)} - one per ${column}.csv`,
    `${fileName} with one row per ${column}, keeping the first of each.`,
  );
  const dedupe = useDedupeSheet(sheetId);
  const matchLeads = useMatchLeads(sheetId);
  const words = useVocab();
  const navigate = useNavigate();
  const [addressCol, setAddressCol] = useState<string | null>(null);

  function submitName() {
    const onError = (e: unknown) =>
      notifications.show({
        color: 'red',
        title: 'Could not change the columns',
        message: (e as Error).message,
      });
    const done = { onSuccess: () => setAsking(null), onError };
    if (asking?.kind === 'rename') rename.mutate({ from: column, to: name.trim() }, done);
    else addColumn.mutate(name.trim(), done);
  }

  /*
    Trim, then optionally lower, then hash — in that order, awaited one at a
    time.

    The rules are stored with an incrementing position and applied in it, so
    firing these together could interleave them. Getting the order wrong is
    silent: trimming a hex string does nothing and leaves the digest of the
    untrimmed value, which still looks like a valid hash and matches nobody.

    Lower-casing is a choice rather than a given. Everybody lower-cases an
    email, so it is on by default; account references have no such convention,
    and lower-casing one the partner hashed with its capitals means nothing
    matches.
  */
  async function applyHash(algorithm: 'md5' | 'sha256', alsoLower: boolean) {
    try {
      await add.mutateAsync({ col: column, kind: 'trim', baseVersion });
      if (alsoLower) await add.mutateAsync({ col: column, kind: 'lower', baseVersion });
      await add.mutateAsync({ col: column, kind: algorithm, baseVersion });
      setAsking(null);
      notifications.show({
        color: 'teal',
        title: `${column} hashed`,
        message: `${algorithm.toUpperCase()} replaced what ${column} held. The version before this is kept.`,
      });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not hash that column',
        message: (e as Error).message,
      });
    }
  }

  function apply(kind: string, extra?: { find?: string; replace?: string }) {
    add.mutate(
      { col: column, kind, ...extra, baseVersion },
      {
        onSuccess: () => setAsking(null),
        onError: (e) =>
          notifications.show({
            color: 'red',
            title: 'Could not apply that',
            message: (e as Error).message,
          }),
      },
    );
  }

  return (
    <>
      <Menu shadow="md" width={215} position="bottom-start">
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="xs"
            aria-label={`Actions for ${column}`}
          >
            <IconChevronDown size={13} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{column}</Menu.Label>
          {/* Reading actions first: they change nothing, and they are what a
              column header is reached for most of the time. */}
          <Menu.Item leftSection={<IconSortAscending size={14} />} onClick={() => onSort('asc')}>
            Sort A to Z
          </Menu.Item>
          <Menu.Item leftSection={<IconSortDescending size={14} />} onClick={() => onSort('desc')}>
            Sort Z to A
          </Menu.Item>
          <Menu.Item
            leftSection={<IconChartBar size={14} />}
            onClick={() => setAsking({ kind: 'summary' })}
          >
            What is in this column…
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCopy size={14} />}
            onClick={() => setAsking({ kind: 'duplicates' })}
          >
            Find duplicates…
          </Menu.Item>
          <Menu.Item
            leftSection={<IconAddressBook size={14} />}
            onClick={() => { matchLeads.reset(); setAsking({ kind: 'match_leads' }); }}
          >
            {`Find my ${words.many('lead')} in these postcodes…`}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconFilter size={14} />}
            onClick={() => {
              setNeedle('');
              setUpper('');
              setOp('contains');
              setAsking({ kind: 'filter' });
            }}
          >
            Filter…
          </Menu.Item>
          <Divider />
          {DIRECT.map((d) => (
            <Menu.Item
              key={d.kind}
              disabled={disabled}
              leftSection={<d.icon size={14} />}
              onClick={() => apply(d.kind)}
            >
              {d.label}
            </Menu.Item>
          ))}
          <Menu.Item
            disabled={disabled}
            leftSection={<IconCursorText size={14} />}
            onClick={() => { setName(column); setAsking({ kind: 'rename' }); }}
          >
            Rename column…
          </Menu.Item>
          <Menu.Item
            disabled={disabled}
            leftSection={<IconColumnInsertRight size={14} />}
            onClick={() => { setName(''); setAsking({ kind: 'add' }); }}
          >
            Add an empty column…
          </Menu.Item>
          <Menu.Item
            disabled={disabled}
            leftSection={<IconMathFunction size={14} />}
            onClick={onCompute}
          >
            Add a worked-out column…
          </Menu.Item>
          <Menu.Item
            disabled={disabled}
            leftSection={<IconMathFunction size={14} />}
            onClick={onFormula}
          >
            Fill a column with a formula…
          </Menu.Item>
          {/*
            Hashing, in place and with the normalising in front of it.

            Three rules rather than one, applied in order: trim, lower, then
            the digest. That is the order a partner used, and getting it wrong
            is silent — hashing first and trimming after trims a hex string,
            which does nothing, and leaves the digest of the untrimmed value
            looking perfectly valid and matching nobody.

            It replaces the column rather than adding one beside it. A file
            holding both an address and its digest is a lookup table for that
            list, and exporting one gives away exactly what hashing it was for.
            Keeping the name also keeps the import mapping working.

            MD5 first, because it is what this industry exchanges.
          */}
          <Menu.Item
            disabled={disabled}
            leftSection={<IconLock size={14} />}
            onClick={() => setAsking({ kind: 'hash', algorithm: 'md5' })}
          >
            Hash this column (MD5)…
          </Menu.Item>
          <Menu.Item
            disabled={disabled}
            leftSection={<IconLock size={14} />}
            onClick={() => setAsking({ kind: 'hash', algorithm: 'sha256' })}
          >
            Hash this column (SHA-256)…
          </Menu.Item>
          <Menu.Item
            disabled={disabled || !canMoveLeft}
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => onMove(-1)}
          >
            Move left
          </Menu.Item>
          <Menu.Item
            disabled={disabled || !canMoveRight}
            leftSection={<IconArrowRight size={14} />}
            onClick={() => onMove(1)}
          >
            Move right
          </Menu.Item>
          <Divider />
          <Menu.Item
            disabled={disabled}
            leftSection={<IconReplace size={14} />}
            onClick={() => { setFind(''); setReplace(''); setAsking({ kind: 'replace' }); }}
          >
            Find and replace…
          </Menu.Item>
          <Menu.Item
            disabled={disabled}
            leftSection={<IconTextWrap size={14} />}
            onClick={() => { setReplace(''); setAsking({ kind: 'fill_empty' }); }}
          >
            Fill empty cells…
          </Menu.Item>
          <Divider />
          <Menu.Item
            leftSection={<IconPhoneCheck size={14} />}
            onClick={() => setAsking({ kind: 'verify' })}
          >
            Check these numbers
          </Menu.Item>
          <Divider />
          <Menu.Item
            color="red"
            disabled={disabled}
            leftSection={<IconEraser size={14} />}
            onClick={() => apply('clear')}
          >
            Empty the column
          </Menu.Item>
          <Menu.Item
            color="red"
            disabled={disabled}
            leftSection={<IconColumnRemove size={14} />}
            onClick={() =>
              dropColumn.mutate(column, {
                onError: (e) =>
                  notifications.show({
                    color: 'red',
                    title: 'Could not remove that column',
                    message: (e as Error).message,
                  }),
              })
            }
          >
            Remove the column
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={asking?.kind === 'summary'}
        onClose={() => setAsking(null)}
        title={`What is in ${column}`}
        size="md"
      >
        <Stack gap="xs">
          {summary.isPending && (
            <Group gap="sm"><Loader size="xs" /><Text size="sm" c="dimmed">Counting…</Text></Group>
          )}
          {summary.error && <Text size="sm" c={TOKENS.redText}>{summary.error.message}</Text>}
          {summary.data && (
            <>
              <Text size="sm">
                The {summary.data.values.length} commonest values, covering{' '}
                {summary.data.rows_covered.toLocaleString()} rows.
              </Text>
              <ScrollArea.Autosize mah={320}>
                <Table>
                  <Table.Tbody>
                    {summary.data.values.map((v: { value: string; rows: number }) => (
                      <Table.Tr key={v.value}>
                        <Table.Td>
                          <Text size="sm">{v.value === '' ? '(empty)' : v.value}</Text>
                        </Table.Td>
                        <Table.Td w={110} ta="right">
                          <Text size="sm" c="dimmed">{v.rows.toLocaleString()} rows</Text>
                        </Table.Td>
                        <Table.Td w={80}>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            onClick={() => {
                              onFilter({
                                col: column,
                                op: v.value === '' ? 'empty' : 'eq',
                                value: v.value,
                              });
                              setAsking(null);
                            }}
                          >
                            Show
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={asking?.kind === 'match_leads'}
        onClose={() => setAsking(null)}
        title={`Find my ${words.many('lead')} in the postcodes in ${column}`}
        size="md"
      >
        {!matchLeads.data && (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              {`Looks up every postcode in this column against your ${words.many('lead')} and puts the ones it finds in a new file. Spacing and capitals are ignored, so \u201cnn3 8qx\u201d and \u201cNN38QX\u201d count as the same place.`}
            </Text>
            {/* A postcode is a street, not a house. Naming an address column
                narrows it to the door number, which on real data removes about
                one match in five as a different property. */}
            <Select
              label="Also match the house number (optional)"
              description="Pick the column with the address line. Without it, everyone in the postcode matches."
              placeholder="Postcode only"
              clearable
              data={columns.filter((c) => c !== column)}
              value={addressCol}
              onChange={setAddressCol}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setAsking(null)}>Cancel</Button>
              <Button
                loading={matchLeads.isPending}
                onClick={() =>
                  matchLeads.mutate(
                    { col: column, addressCol: addressCol ?? undefined },
                    {
                      onError: (e) =>
                        notifications.show({
                          color: 'red',
                          title: 'Could not match those postcodes',
                          message: (e as Error).message,
                        }),
                    },
                  )
                }
              >
                Find them
              </Button>
            </Group>
          </Stack>
        )}
        {matchLeads.data && (
          <Stack gap="sm">
            {/* The failure this feature makes quietly. A column holding
                postcodes half the time finds half the leads and still reads as
                a working answer — which is exactly what happened on a file
                whose "AddressLine3" is the postcode on short addresses and a
                locality on long ones. Said before the numbers, because after
                them it reads as a footnote to a result already believed. */}
            {matchLeads.data.source_postcode_shaped < matchLeads.data.source_postcodes * 0.9 && (
              <Alert color="orange" title="This column may not be the postcodes">
                <Text size="sm">
                  Only {formatNumber(matchLeads.data.source_postcode_shaped)} of the{' '}
                  {formatNumber(matchLeads.data.source_postcodes)} values in <b>{column}</b>
                  {` look like a UK postcode, so this found ${words.many('lead')} for the ones that do and missed the rest. If another column holds the postcode, run it on that one instead.`}
                </Text>
              </Alert>
            )}
            <Text size="sm">
              <b>{formatNumber(matchLeads.data.matched_leads)}</b>{` of your ${words.many('lead')} are in `}
              <b>{formatNumber(matchLeads.data.matched_postcodes)}</b> of the{' '}
              {formatNumber(matchLeads.data.source_postcodes)} postcodes in this column.
            </Text>
            {/* Consent is shown next to the total, not tucked away. Most leads
                carry no consent value at all, so a bare match count reads as a
                contact list when most of it is not one. */}
            {(matchLeads.data.different_property > 0 || matchLeads.data.no_house_number > 0) && (
              <Text size="sm" c="dimmed">
                Left out: {formatNumber(matchLeads.data.different_property)} in a matching
                postcode but at a different house number, and{' '}
                {formatNumber(matchLeads.data.no_house_number)} with no number on the
                address to compare.
              </Text>
            )}
            <Alert
              color={matchLeads.data.with_sms_consent === 0 ? 'red' : 'yellow'}
              title="Who you can actually contact"
            >
              <Text size="sm">
                {formatNumber(matchLeads.data.with_sms_consent)} agreed to be texted and{' '}
                {formatNumber(matchLeads.data.with_email_consent)} agreed to be emailed.
                The rest either said no or never recorded an answer. The file has a
                column for each, so you can filter on it before you send anything.
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setAsking(null)}>Close</Button>
              <Button
                onClick={() => {
                  setAsking(null);
                  navigate(`/sheets/${matchLeads.data!.sheet_id}`);
                }}
              >
                Open the file
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={asking?.kind === 'duplicates'}
        onClose={() => setAsking(null)}
        title={`Repeated values in ${column}`}
        size="md"
      >
        <Stack gap="xs">
          {duplicates.isPending && (
            <Group gap="sm"><Loader size="xs" /><Text size="sm" c="dimmed">Looking through the file…</Text></Group>
          )}
          {duplicates.error && (
            <Text size="sm" c={TOKENS.redText}>{duplicates.error.message}</Text>
          )}
          {duplicates.data && duplicates.data.values.length === 0 && (
            <Text size="sm">Every value in this column is unique.</Text>
          )}
          {duplicates.data && duplicates.data.values.length > 0 && (
            <>
              <Text size="sm">
                {duplicates.data.repeated_rows.toLocaleString()} rows share a value with another
                row. The most repeated:
              </Text>
              <ScrollArea.Autosize mah={300}>
                <Table>
                  <Table.Tbody>
                    {duplicates.data.values.map((v) => (
                      <Table.Tr key={v.value}>
                        <Table.Td>
                          <Text size="sm">{v.value === '' ? '(empty)' : v.value}</Text>
                        </Table.Td>
                        <Table.Td w={90} ta="right">
                          <Text size="sm" c="dimmed">{v.rows.toLocaleString()} rows</Text>
                        </Table.Td>
                        <Table.Td w={80}>
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            onClick={() => {
                              onFilter({ col: column, op: 'eq', value: v.value });
                              setAsking(null);
                            }}
                          >
                            Show
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea.Autosize>

              <NewFileFields details={dedupeDetails} sourceName={fileName} />
              <Group justify="flex-end">
                <Button variant="subtle" onClick={() => setAsking(null)}>Close</Button>
                <Button
                  disabled={disabled}
                  loading={dedupe.isPending}
                  onClick={() =>
                    dedupe.mutate(
                      {
                        cols: [column],
                        keepLast: false,
                        baseVersion,
                        name: dedupeDetails.name,
                        description: dedupeDetails.description,
                      },
                      {
                        onSuccess: (made) => {
                          setAsking(null);
                          openNewFile(made, fileName);
                        },
                        onError: (e) =>
                          notifications.show({
                            color: 'red',
                            title: 'Could not remove duplicates',
                            message: (e as Error).message,
                          }),
                      },
                    )
                  }
                >
                  Save the first of each as a new file
                </Button>
              </Group>
              <Text size="xs" c="dimmed">
                Rows with nothing in this column are all kept, because they are unknown
                rather than identical.
              </Text>
            </>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={asking?.kind === 'rename' || asking?.kind === 'add'}
        onClose={() => setAsking(null)}
        title={asking?.kind === 'rename' ? `Rename ${column}` : 'Add a column'}
        size="sm"
      >
        <Stack>
          <TextInput
            data-autofocus
            label="Name"
            description="Letters, numbers and underscores. Anything else becomes an underscore."
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submitName(); }}
          />
          <Text size="xs" c="dimmed">
            {asking?.kind === 'rename'
              ? 'The file itself is not rewritten. The new name is written in when you apply changes to the file.'
              : 'The column starts empty. Fill it by editing cells, and it is written in when you apply changes to the file.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAsking(null)}>Cancel</Button>
            <Button
              loading={rename.isPending || addColumn.isPending}
              disabled={!name.trim()}
              onClick={submitName}
            >
              {asking?.kind === 'rename' ? 'Rename' : 'Add'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={asking?.kind === 'filter'}
        onClose={() => setAsking(null)}
        title={`Filter by ${column}`}
        size="sm"
      >
        <Stack>
          <Select
            label="Where this column"
            data={OPS}
            value={op}
            onChange={(v) => setOp(v ?? 'contains')}
            allowDeselect={false}
          />
          {/* `empty` and `not_empty` take no value, so asking for one would
              be a box that does nothing. */}
          {op !== 'empty' && op !== 'not_empty' && (
            <TextInput
              data-autofocus
              label={op === 'between' ? 'From' : 'Value'}
              description={
                NUMERIC.has(op)
                  ? 'A number. Currency symbols and commas are ignored.'
                  : undefined
              }
              value={needle}
              onChange={(e) => setNeedle(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && ready) applyFilter();
              }}
            />
          )}
          {op === 'between' && (
            <TextInput
              label="To"
              description="Both ends are included."
              value={upper}
              onChange={(e) => setUpper(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && ready) applyFilter();
              }}
            />
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAsking(null)}>Cancel</Button>
            <Button disabled={!ready} onClick={applyFilter}>
              Apply filter
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={asking?.kind === 'verify'}
        onClose={() => setAsking(null)}
        title={`Check the numbers in ${column}`}
        size="sm"
      >
        <Stack>
          <Text size="sm">
            Every value in this column is checked against its mobile network. That costs one
            lookup per row and takes real time on a large file, so it runs in the background
            and the results appear on the Verify screen.
          </Text>
          <Text size="sm" c="dimmed">
            This uses the column as it currently reads, including any changes you have made.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAsking(null)}>Cancel</Button>
            <Button
              loading={verify.isPending}
              onClick={() =>
                verify.mutate(column, {
                  onSuccess: () => {
                    setAsking(null);
                    notifications.show({
                      color: 'teal',
                      title: 'Checking started',
                      message: 'Progress and results are on the Verify screen.',
                    });
                  },
                  onError: (e) =>
                    notifications.show({
                      color: 'red',
                      title: 'Could not start checking',
                      message: (e as Error).message,
                    }),
                })
              }
            >
              Start checking
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/*
        Opened by a list of the kinds this modal handles, not by "anything not
        in that list". The negative form was a trap: every new dialog opened
        this one too unless someone remembered to add its name to the
        exclusions. `hash` already failed that way — hashing a column opened
        this modal underneath it, invisible only because the hash modal is
        rendered later and drew on top. `match_leads` is rendered earlier, so
        it lost, and "find my leads in these postcodes" showed "Fill empty
        cells" instead. A positive list cannot fail like that.
      */}
      <Modal
        opened={asking !== null && ['replace', 'fill_empty', 'clear'].includes(asking.kind)}
        onClose={() => setAsking(null)}
        title={asking?.kind === 'replace' ? `Replace in ${column}` : `Fill empty cells in ${column}`}
        size="sm"
      >
        <Stack>
          {asking?.kind === 'replace' && (
            <TextInput
              data-autofocus
              label="Find"
              description="Every occurrence in this column, not whole cells only"
              value={find}
              onChange={(e) => setFind(e.currentTarget.value)}
            />
          )}
          <TextInput
            data-autofocus={asking?.kind === 'fill_empty'}
            label={asking?.kind === 'replace' ? 'Replace with' : 'Value'}
            description={
              asking?.kind === 'replace' ? 'Leave empty to delete it' : 'Used only where the cell is empty'
            }
            value={replace}
            onChange={(e) => setReplace(e.currentTarget.value)}
          />
          <Text size="xs" c="dimmed">
            This applies to every row in the file, and can be taken back from the changes list.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setAsking(null)}>Cancel</Button>
            <Button
              loading={add.isPending}
              disabled={asking?.kind === 'replace' && find === ''}
              onClick={() => apply(asking!.kind, { find, replace })}
            >
              Apply
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/*
        Confirmed rather than applied straight from the menu, unlike trim and
        lowercase, because it is the one rule that makes a column nobody can
        read afterwards. The version before it is kept, so it is undoable —
        but it is worth seeing before it happens rather than after.
      */}
      <Modal
        opened={asking?.kind === 'hash'}
        onClose={() => setAsking(null)}
        title={`Hash ${column}`}
        size="md"
      >
        <Stack>
          <Text size="sm">
            Replaces what {column} holds with its{' '}
            {asking?.kind === 'hash' && asking.algorithm === 'sha256' ? 'SHA-256' : 'MD5'} digest,
            keeping the column where it is. Spaces are trimmed off both ends first, because a
            trailing space changes a digest completely.
          </Text>
          <Switch
            label="Lower-case it first"
            description="Right for email addresses, which everyone lower-cases. Wrong for a reference the other side hashed with its capitals."
            checked={hashLower}
            onChange={(e) => setHashLower(e.currentTarget.checked)}
          />
          <Alert color="chartAmber" variant="light">
            <Text size="sm">
              Nobody can read the column afterwards, which is the point: a file holding both a
              value and its digest is a lookup table for that list. The version before this is
              kept, so the old values can be got back.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAsking(null)}>Cancel</Button>
            <Button
              loading={add.isPending}
              onClick={() =>
                asking?.kind === 'hash' && applyHash(asking.algorithm, hashLower)
              }
            >
              Hash {column}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
